import { ChakraProvider } from "@chakra-ui/react";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import GoogleAnalyticsProvider from "@providers/analytic/GoogleAnalytics";
import { AuthProvider } from "@providers/auth";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NextIntlProvider } from "next-intl";
import type { AppProps } from "next/app";
import Head from "next/head";
import { theme } from "styles/theme";
import "../styles/globals.scss";

const HOST = process.env.NEXT_PUBLIC_HOST || "https://app.moongate.id";
if (!HOST) throw "Missing NEXT_PUBLIC_HOST";

const API_HOST = process.env.NEXT_PUBLIC_API_HOST || "";
if (!API_HOST) throw "Missing NEXT_PUBLIC_API_HOST";

const API_KEY = process.env.NEXT_PUBLIC_API_KEY || "";
if (!API_KEY) throw "Missing NEXT_PUBLIC_API_KEY";

const FIREBASE_PROJECT_ID = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || "";
if (!FIREBASE_PROJECT_ID) throw "Missing NEXT_PUBLIC_FIREBASE_PROJECT_ID";

const FIREBASE_API_KEY = process.env.NEXT_PUBLIC_FIREBASE_API_KEY || "";
if (!FIREBASE_API_KEY) throw "Missing NEXT_PUBLIC_FIREBASE_API_KEY";

const FIREBASE_APP_ID = process.env.NEXT_PUBLIC_FIREBASE_APP_ID || "";
if (!FIREBASE_APP_ID) throw "Missing NEXT_PUBLIC_FIREBASE_APP_ID";

const PARTICLE_PROJECT_ID = process.env.NEXT_PUBLIC_PARTICLE_PROJECT_ID || "";
if (!PARTICLE_PROJECT_ID) throw "Missing NEXT_PUBLIC_PARTICLE_PROJECT_ID";

const PARTICLE_CLIENT_KEY = process.env.NEXT_PUBLIC_PARTICLE_CLIENT_KEY || "";
if (!PARTICLE_CLIENT_KEY) throw "Missing NEXT_PUBLIC_PARTICLE_CLIENT_KEY";

const PARTICLE_APP_ID = process.env.NEXT_PUBLIC_PARTICLE_APP_ID || "";
if (!PARTICLE_APP_ID) throw "Missing NEXT_PUBLIC_PARTICLE_APP_ID";

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || "";
const PAYPAL_CLIENT_ID = process.env.NEXT_PUBLIC_PAYPAL_APP_CLIENT_ID || "";
const CURRENCY = process.env.NEXT_PUBLIC_CURRENCY || "USD";

const AUTH_FALLBACK_PATH = "/";
const PUBLIC_ROUTES_WITH_AUTH = [/.*/];
const PUBLIC_ROUTES_NO_AUTH = [];

function App({ Component, pageProps }: AppProps<any>) {
  const queryClient = new QueryClient();
  return (
    <>
      <GoogleAnalyticsProvider measurementId={GA_MEASUREMENT_ID}>
        <ChakraProvider theme={theme}>
          <NextIntlProvider
            formats={{
              dateTime: {
                short: {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                },
              },
            }}
            messages={pageProps.messages}
            now={new Date(pageProps.now)}
            timeZone="Asia/Hong_Kong"
          >
            <QueryClientProvider client={queryClient}>
              <PayPalScriptProvider
                options={{
                  "client-id": PAYPAL_CLIENT_ID,
                  currency: CURRENCY,
                }}
              >
                <AuthProvider
                  appHost={HOST}
                  apiHost={API_HOST}
                  apiKey={API_KEY}
                  firebaseProjectId={FIREBASE_PROJECT_ID}
                  firebaseAppId={FIREBASE_APP_ID}
                  firebaseAPIKey={FIREBASE_API_KEY}
                  particleProjectId={PARTICLE_PROJECT_ID}
                  particleClientKey={PARTICLE_CLIENT_KEY}
                  particleAppId={PARTICLE_APP_ID}
                  authFallbackPath={AUTH_FALLBACK_PATH}
                  publicRoutesWithAuth={PUBLIC_ROUTES_WITH_AUTH}
                  publicRoutesNoAuth={PUBLIC_ROUTES_NO_AUTH}
                >
                  <Head>
                    <meta
                      name="viewport"
                      content="width=device-width, height=device-height, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"
                    />
                  </Head>
                  <Component {...pageProps} />
                </AuthProvider>
              </PayPalScriptProvider>
            </QueryClientProvider>
          </NextIntlProvider>
        </ChakraProvider>
      </GoogleAnalyticsProvider>
    </>
  );
}

export default App;
